import CustomerDetailsForm from 'components/pages/CustomerDetailsForm';
import KycForm from 'components/pages/KycForm';
import Header from 'components/shared/Header';
import { useAppContext } from 'context/useAppContext';
import { FC, useEffect, useMemo, useCallback, useRef } from 'react';
import { IAppInit } from 'types';
import useSWRMutation from 'swr/mutation';
import axiosInstance from 'utils/axiosInstance';
import { ReactComponent as Spinner } from './assets/spinner.svg';
import { ReactComponent as ErrorSvg } from './assets/error.svg';
import Icon from 'components/shared/Icon';
import { toMinorDenomination } from 'utils/helpers';
import axios from 'axios';
import Button from 'components/ui/Button';

interface IFetcherParams {
  arg: {
    amount: number;
    currency: string;
    reference: string;
  };
}

async function initiateFetcher(url: string, { arg }: IFetcherParams) {
  const response = await axiosInstance.post(url, arg);
  return response.data;
}

const PARENT_ORIGIN =
  process.env.REACT_APP_PARENT_ORIGIN || window.location.origin;

const App: FC<IAppInit> = (props) => {
  const { trigger, isMutating, error, data } = useSWRMutation(
    '/onlending/transactions/initiate',
    initiateFetcher,
  );

  const {
    updateFormData,
    updateAppData,
    currentStep,
    setError,
    error: errMsg,
    clearError,
  } = useAppContext();

  const hasFetchedRef = useRef(false);

  // Memoize form-related props
  const memoizedFormProps = useMemo(
    () => ({
      firstName: props.firstName,
      lastName: props.lastName,
      dateOfBirth: props.dateOfBirth,
      emailAddress: props.emailAddress,
      ticketPrice: props.ticketPrice,
      bookingRef: props.bookingRef,
      gender: props.gender,
      phoneNumber: props.phoneNumber,
    }),
    [
      props.firstName,
      props.lastName,
      props.dateOfBirth,
      props.emailAddress,
      props.ticketPrice,
      props.bookingRef,
      props.gender,
      props.phoneNumber,
    ],
  );

  // Memoize app-related props
  const memoizedAppProps = useMemo(
    () => ({
      mode: props.mode,
      publicKey: props.publicKey,
    }),
    [props.mode, props.publicKey],
  );

  const fetchTransactionData = useCallback(async () => {
    try {
      const response = await trigger({
        amount: toMinorDenomination(memoizedFormProps.ticketPrice),
        currency: 'NGN',
        reference: memoizedFormProps.bookingRef,
      });

      if (response) {
        updateAppData({ transactionId: response.transactionId });
      }
    } catch (err) {
      let errorMessage =
        'An error occurred while starting the SDK. Please try again.';

      if (axios.isAxiosError(err) && err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    }
  }, [
    trigger,
    memoizedFormProps.ticketPrice,
    memoizedFormProps.bookingRef,
    updateAppData,
    setError,
  ]);

  const handleRetry = useCallback(() => {
    clearError();
    fetchTransactionData();
  }, [clearError, fetchTransactionData]);

  const handleCancel = useCallback(() => {
    window.parent.postMessage({ message: 'onLendingClose' }, PARENT_ORIGIN);
  }, []);

  // Initialize form and app data from props
  useEffect(() => {
    const hasFormData = Object.values(memoizedFormProps).some(
      (val) => val !== '' && val !== undefined && val !== null,
    );
    const hasAppData = Object.values(memoizedAppProps).some(
      (val) => val !== '' && val !== undefined && val !== null,
    );

    if (hasFormData || hasAppData) {
      if (hasFormData) {
        updateFormData(memoizedFormProps);
      }
      if (hasAppData) {
        updateAppData(memoizedAppProps);
      }
    }
  }, [memoizedFormProps, memoizedAppProps, updateFormData, updateAppData]);

  useEffect(() => {
    if (hasFetchedRef.current || !memoizedFormProps.bookingRef) {
      return;
    }

    hasFetchedRef.current = true;
    fetchTransactionData();
  }, [memoizedFormProps.bookingRef, fetchTransactionData]);

  return (
    <div className="font-primary flex w-screen h-screen max-h-screen lg:bg-primary-200/70 bg-white justify-center items-center overflow-hidden py-6 px-6 lg:p-12">
      <div className="w-full max-w-[888px] h-[860px] md:max-h-full bg-white lg:shadow-lg lg:rounded-lg flex flex-col gap-2 pt-4 md:gap-4 md:p-7">
        <Header />

        {!data && isMutating && (
          <div className="flex flex-col items-center justify-center h-[calc(100vh-80px)]">
            <Spinner
              className="w-20 h-20 animate-spin"
              aria-label="Loading transaction data"
            />
            <p className="mt-4 text-gray-600">Loading...</p>
          </div>
        )}

        {data && !isMutating && (
          <>
            {currentStep === 1 && <CustomerDetailsForm />}
            {currentStep === 2 && <KycForm />}
          </>
        )}

        {!isMutating && error && (
          <div className="flex flex-col items-center justify-center h-full pb-12 px-4">
            <Icon
              svg={ErrorSvg}
              className="w-24 h-24 lg:w-32 text-red-600 mt-0.5 mr-3 flex-shrink-0"
            />
            <div
              className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md w-full"
              role="alert"
              aria-live="assertive"
            >
              <div className="flex items-center justify-center">
                <p className="text-red-800 text-lg text-center">
                  {errMsg || 'An error occurred. Please try again.'}
                </p>
              </div>
            </div>
            <div className="flex gap-3 mt-24 md:mt-10 w-full max-w-md">
              <Button
                onClick={handleCancel}
                className="flex-1 !text-lg"
                variant="outline"
                aria-label="Cancel transaction"
              >
                Cancel
              </Button>
              <Button
                onClick={handleRetry}
                className="flex-1 !text-lg"
                variant="primary"
                aria-label="Retry transaction"
              >
                Retry
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default App;
